import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { btnPrimarySx, btnDisabledSx } from '../../../utils/buttonSx';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import { usePageHeader } from '../../../context/PageHeaderContext';
import { scrollAreaSx } from '../../../utils/scrollSx';
import { useConfirm } from '../../../hooks/useConfirm';

interface ScheduleRow { id: number; name: string; eventCount: number; videoCount: number; imageCount: number; updatedAt: string; }

const glass = { background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)' } as const;

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

const IcoPlus = () => <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoX    = () => <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoCopy = () => <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IcoCal  = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IcoCalLg = () => <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" opacity={.25}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;

// Violet accent for schedules (matches playlists' indigo)
const T_BG     = 'rgba(99,102,241,.1)';
const T_BORDER = 'rgba(99,102,241,.2)';
const T_TEXT   = '#818cf8';

const SchedulesPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);
  const navigate = useNavigate();
  const { setBreadcrumbs, setActionConfig } = usePageHeader();
  const isNarrow = useMediaQuery('(max-width:899px)');
  const { confirm, confirmModal } = useConfirm();

  const [schedules, setSchedules]   = useState<ScheduleRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [creating, setCreating]     = useState(false);
  const [listView, setListView]     = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [blockMsg, setBlockMsg]     = useState<string | null>(null);
  const [editingId, setEditingId]   = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  const startRename = (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation();
    setEditingId(id); setEditingName(name);
  };
  const commitRename = async (id: number) => {
    const name = editingName.trim();
    setEditingId(null);
    if (!name) return;
    try {
      await axios.patch(`/schedules/${id}`, { name });
      setSchedules(prev => prev.map(s => s.id === id ? { ...s, name } : s));
    } catch {}
  };

  useEffect(() => {
    setBreadcrumbs([{ label: t('schedules.title') }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs]);

  // Mobile: the create button moves into the topbar
  useEffect(() => {
    if (!isNarrow) { setActionConfig(null); return; }
    setActionConfig({ label: 'New Schedule', onClick: handleCreate, disabled: creating });
    return () => setActionConfig(null);
  }, [isNarrow, creating, setActionConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<ScheduleRow[]>('/schedules');
      setSchedules(data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (auth) fetchSchedules(); }, [auth, fetchSchedules]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post<ScheduleRow>('/schedules', { name: 'New Schedule' });
      setSchedules(prev => [data, ...prev]);
      navigate(`/schedules/${data.id}`);
    } catch {}
    finally { setCreating(false); }
  };

  const describeBlock = (err: any): string | null => {
    const body = err?.response?.data;
    if (err?.response?.status !== 409 || body?.code !== 'IN_USE') return null;
    const u = body.usages ?? {};
    const lines: string[] = [];
    for (const s of u.screens ?? []) lines.push(`• ${s.displayName || s.deviceId}`);
    return lines.join('\n') || 'In use';
  };

  const handleDuplicate = async (id: number, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post<ScheduleRow>(`/schedules/${id}/duplicate`, { name: `${name} (copy)` });
      setSchedules(prev => [data, ...prev]);
    } catch {}
  };

  const handleDelete = async (id: number, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await confirm({ title: `Delete "${name}"?`, message: 'All events will be removed.', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' });
    if (!ok) return;
    try {
      await axios.delete(`/schedules/${id}`);
      setSchedules(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      setBlockMsg(describeBlock(err));
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: { xs: '12px', md: '20px' }, gap: '16px' }}>

      {/* Toolbar — hidden on mobile: the count only ate vertical space and the
           create button now lives in the topbar */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{schedules.length} schedules</Typography>
        <Box sx={{ flex: 1 }} />

        {/* Grid / List toggle — desktop only */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, border: '1px solid var(--glass-border)', borderRadius: '8px', overflow: 'hidden' }}>
          {[false, true].map(isListMode => (
            <Box key={String(isListMode)} component="button" onClick={() => setListView(isListMode)}
              title={isListMode ? 'List view' : 'Grid view'}
              sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .12s', bgcolor: listView === isListMode ? 'var(--selected-bg)' : 'transparent', color: listView === isListMode ? 'var(--text-primary)' : 'var(--text-disabled)', '&:hover': { color: 'var(--text-primary)' } }}>
              {isListMode
                ? <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                : <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>}
            </Box>
          ))}
        </Box>

        <Box component="button" onClick={handleCreate} disabled={creating}
          sx={{ ...btnPrimarySx, ...(creating ? btnDisabledSx : {}), display: { xs: 'none', md: 'inline-flex' } }}>
          <IcoPlus /> New Schedule
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--glass-border)', borderRadius: 4 }, ...scrollAreaSx }}>
        {loading ? (
          <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)', py: '40px', textAlign: 'center' }}>Loading…</Typography>
        ) : !loading && schedules.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 280, gap: 1.5, opacity: .4 }}>
            <IcoCalLg />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>No schedules yet</Typography>
          </Box>
        ) : listView && !isMobile ? (
          /* List view */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {schedules.map(s => (
              <Box key={s.id} onClick={() => { if (editingId === s.id) return; navigate(`/schedules/${s.id}`); }}
                sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: '14px', py: '10px', borderRadius: '10px', cursor: editingId === s.id ? 'default' : 'pointer', ...glass, transition: 'border-color .12s, background .12s', '&:hover': { borderColor: T_BORDER, bgcolor: 'var(--glass-hover)' }, '&:hover .sc-rename-btn': { opacity: 1 } }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: T_BG, border: `1px solid ${T_BORDER}`, color: T_TEXT }}>
                  <IcoCal />
                </Box>
                {editingId === s.id ? (
                  <Box component="input" autoFocus value={editingName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingName(e.target.value)}
                    onBlur={() => commitRename(s.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') commitRename(s.id); if (e.key === 'Escape') setEditingId(null); }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    sx={{ flex: 1, minWidth: 0, background: 'var(--input-bg)', border: `1px solid ${T_TEXT}`, borderRadius: '5px', outline: 'none', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'inherit', px: '6px', py: '3px' }} />
                ) : (
                  <>
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</Typography>
                    <Box component="button" className="sc-rename-btn" onClick={e => startRename(e, s.id, s.name)}
                      title="Rename" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '4px', background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', opacity: 0, flexShrink: 0, transition: 'opacity .1s', fontSize: 12, '&:hover': { color: T_TEXT } }}>✏</Box>
                    <Box component="button" className="sc-rename-btn" onClick={e => handleDuplicate(s.id, s.name, e)}
                      title={t('schedules.duplicate')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '4px', background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', opacity: 0, flexShrink: 0, transition: 'opacity .1s', '&:hover': { color: T_TEXT } }}><IcoCopy /></Box>
                  </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '2px', borderRadius: '6px', bgcolor: T_BG, border: `1px solid ${T_BORDER}` }}>
                  <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke={T_TEXT} strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: T_TEXT }}>{s.eventCount}</Typography>
                </Box>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', minWidth: 60, textAlign: 'right', flexShrink: 0 }}>{fmtDate(s.updatedAt)}</Typography>
                <Box component="button" onClick={e => handleDelete(s.id, s.name, e)}
                  sx={{ display: 'flex', width: 26, height: 26, alignItems: 'center', justifyContent: 'center', borderRadius: '6px', background: 'none', border: 'none', color: 'var(--text-disabled)', cursor: 'pointer', opacity: .4, flexShrink: 0, transition: 'all .1s', '&:hover': { opacity: 1, color: '#f87171' } }}><IcoX /></Box>
              </Box>
            ))}
          </Box>
        ) : (
          /* Grid view */
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {schedules.map(s => (
              <Box key={s.id} onClick={() => { if (editingId === s.id) return; navigate(`/schedules/${s.id}`); }}
                sx={{ borderRadius: '14px', cursor: editingId === s.id ? 'default' : 'pointer', ...glass, display: 'flex', flexDirection: 'column', gap: '8px', p: '12px', minHeight: 110, transition: 'border-color .15s', '&:hover': { borderColor: T_BORDER }, '&:hover .sc-rename-btn': { opacity: 1 } }}>

                {/* Row 1: name + ✏ + ✕ inline */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                  {editingId === s.id ? (
                    <Box component="input" autoFocus value={editingName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingName(e.target.value)}
                      onBlur={() => commitRename(s.id)}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') commitRename(s.id); if (e.key === 'Escape') setEditingId(null); }}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{ flex: 1, minWidth: 0, background: 'var(--input-bg)', border: `1px solid ${T_TEXT}`, borderRadius: '5px', outline: 'none', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'inherit', px: '6px', py: '3px' }} />
                  ) : (
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</Typography>
                  )}
                  <Box component="button" className="sc-rename-btn" onClick={e => startRename(e, s.id, s.name)}
                    title="Rename" sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: '6px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--text-disabled)', opacity: 0, transition: 'all .15s', '&:hover': { color: T_TEXT, bgcolor: T_BG, opacity: 1 } }}>✏</Box>
                  <Box component="button" className="sc-rename-btn" onClick={e => handleDuplicate(s.id, s.name, e)}
                    title={t('schedules.duplicate')} sx={{ flexShrink: 0, width: 24, height: 24, borderRadius: '6px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-disabled)', opacity: 0, transition: 'all .15s', '&:hover': { color: T_TEXT, bgcolor: T_BG, opacity: 1 } }}><IcoCopy /></Box>
                  <Box component="button" onClick={e => handleDelete(s.id, s.name, e)}
                    sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '7px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--text-disabled)', opacity: .5, transition: 'all .15s', '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,.12)', opacity: 1 } }}>
                    <IcoX />
                  </Box>
                </Box>

                {/* Row 2: events count */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke={T_TEXT} strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T_TEXT }}>{s.eventCount} events</Typography>
                </Box>

                {/* Row 3: video + image counts */}
                <Box sx={{ display: 'flex', gap: '14px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ fontSize: 11 }}>🎬</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-disabled)' }}>{s.videoCount} video</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography sx={{ fontSize: 11 }}>🖼</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-disabled)' }}>{s.imageCount} image</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {/* New schedule tile */}
            <Box onClick={handleCreate}
              sx={{ borderRadius: '14px', border: `1.5px dashed ${T_BORDER}`, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: 120, color: `rgba(20,184,166,.5)`, transition: 'all .15s', '&:hover': { borderColor: T_TEXT, background: T_BG, color: T_TEXT } }}>
              <Box sx={{ fontSize: 22 }}>+</Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>New Schedule</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* "In use" block modal */}
      {blockMsg && (
        <Box onClick={() => setBlockMsg(null)} sx={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)' }}>
          <Box onClick={e => e.stopPropagation()} sx={{ width: 380, maxWidth: '90vw', ...glass, borderRadius: '16px', overflow: 'hidden' }}>
            <Box sx={{ px: '20px', py: '14px', borderBottom: '1px solid var(--glass-border)', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Schedule is in use</Box>
            <Box sx={{ px: '20px', py: '16px', fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{blockMsg}</Box>
            <Box sx={{ px: '20px', py: '14px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end' }}>
              <Box component="button" onClick={() => setBlockMsg(null)} sx={{ px: '12px', py: '6px', borderRadius: '8px', ...glass, border: '1px solid var(--glass-border)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Close</Box>
            </Box>
          </Box>
        </Box>
      )}
      {confirmModal}
    </Box>
  );
};

export default SchedulesPage;
